/* eslint-disable react/no-children-prop */
import { Drawer, Stack, useMediaQuery, useTheme } from "@mui/material";
import { useRef } from "react";
import { FormProvider, FormState } from "../../../components/FormProvider";
import { Child, Receipt, ServicePeriod } from "../../../models";
import { useGetReceiptByIdQuery, useSaveReceiptMutation, useSaveServicePeriodMutation } from "../../../redux/apiStore";
import { EditReceiptForm, ReceiptFormData } from "./EditReceiptForm";

interface EditReceiptDrawerProps {
  open: boolean;
  servicePeriod: ServicePeriod | null;
  children: Child[];
  onClose: () => void;
  onSaved: (receiptId: number) => void;
}

export function EditReceiptDrawer(props: EditReceiptDrawerProps) {
  const { open, servicePeriod, children, onClose, onSaved } = props;
  const mode = servicePeriod?.receiptId ? "edit" : "create";
  const formDataGetterRef = useRef<(() => ReceiptFormData | null) | null>(null);
  const resetFormRef = useRef<(() => void) | null>(null);
  const [saveReceipt] = useSaveReceiptMutation();
  const [saveServicePeriod] = useSaveServicePeriodMutation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const receiptQuery = useGetReceiptByIdQuery({ id: servicePeriod?.receiptId ?? undefined }, { skip: !open || !servicePeriod?.receiptId });

  const handleSave = async (form: FormState) => {
    const formData = formDataGetterRef.current ? formDataGetterRef.current() : null;
    form.dismissAlert();

    if (!servicePeriod || !formData) {
      form.notifyError("Receipt form is incomplete.");
      return;
    }

    try {
      form.setDisabled(true);
      const savedReceipt = await saveReceipt(formData.receipt).unwrap();

      if (!servicePeriod.receiptId) {
        await saveServicePeriod({
          ...servicePeriod,
          receiptId: savedReceipt.id,
          family: undefined,
        }).unwrap();
      }

      form.notifySuccess("Receipt saved");
      onSaved(savedReceipt.id);
      onClose();
    } catch (err) {
      form.notifyError("Error saving receipt", err);
    } finally {
      form.setDisabled(false);
    }
  };

  const handleCancel = async (form: FormState) => {
    try {
      resetFormRef.current?.();
      onClose();
    } catch (err) {
      form.notifyError("Error undoing changes", err);
    }
  };

  const receipt: Receipt | null = receiptQuery.data ?? null;

  return (
    <Drawer
      anchor="right"
      open={open}
      sx={{ zIndex: 1550 }}
      slotProps={{
        paper: {
          sx: {
            width: isSmallScreen ? "100%" : "760px",
            zIndex: 1550,
          },
        },
      }}
    >
      <Stack sx={{ p: 2, height: "100%" }}>
        <FormProvider fullHeight onSave={handleSave} onCancel={handleCancel} onDelete={() => {}} showControls={false}>
          <EditReceiptForm
            mode={mode}
            servicePeriod={servicePeriod}
            receipt={receipt}
            children={children}
            isLoading={receiptQuery.isFetching}
            dataGetterRef={formDataGetterRef}
            resetFormRef={resetFormRef}
          />
        </FormProvider>
      </Stack>
    </Drawer>
  );
}
